import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Group,
  Text,
  ActionIcon,
  Paper,
  Box,
  Select,
  Button,
  Tooltip
} from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconLogout,
  IconBuilding,
  IconSwitch
} from '@tabler/icons-react';
import HandoverNotification from '../handover/HandoverNotification';
import LanguageSwitcher from './LanguageSwitcher';
import OperatorStatusToggle from './OperatorStatusToggle';
import zeusLogo from '@assets/Zeus_cerchio-grigio_1767951718971.png';
import { useWebSearchNotifications } from '../../hooks/useWebSearchNotifications';
import { apiClient } from '../../services/authService';
import { useAuth } from '../../hooks/useAuth';
import styles from './ManagementShell.module.css';
import ui from '../../styles/management.module.css';

interface TopBarProps {
  onLogout: () => void;
  onToggleSidebar: () => void;
  sidebarVisible: boolean;
}

interface ClientOption {
  id: number;
  name: string;
}

export default function TopBar({ onLogout, onToggleSidebar, sidebarVisible }: TopBarProps) {
  const { t } = useTranslation();
  const [canHandover, setCanHandover] = useState(false);
  const { isGlobalAdmin, selectedClientId, setSelectedClientId } = useAuth();
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [pendingClientId, setPendingClientId] = useState<number | null>(selectedClientId);

  // Sync pendingClientId when selectedClientId changes externally (e.g., auto-select on mount)
  useEffect(() => {
    setPendingClientId(selectedClientId);
  }, [selectedClientId]);

  // Enable SSE notifications for web search usage warnings
  useWebSearchNotifications({ enabled: true });

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        // 🔒 SECURITY: Token is sent automatically via httpOnly cookie (withCredentials: true)
        const response = await apiClient.get('/api/users/me');

        if (response.data.success && response.data.data) {
          setCanHandover(response.data.data.can_handover ?? false);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    fetchUserProfile();
  }, []);

  // Load clients list for admin_globale client context selector
  useEffect(() => {
    if (!isGlobalAdmin()) return;

    const fetchClients = async () => {
      try {
        const response = await apiClient.get('/api/clients');
        const data = response.data?.data || response.data || [];
        const clientList = Array.isArray(data) ? data : [];
        setClients(clientList.map((c: any) => ({ id: c.id, name: c.name })));

        // Auto-select the first client if none is selected
        if (!selectedClientId && clientList.length > 0) {
          setSelectedClientId(clientList[0].id);
        }
      } catch (error) {
        console.error('Error fetching clients for admin context:', error);
      }
    };

    fetchClients();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box
        className={styles.topbar}
        style={{
          flexShrink: 0,
        }}
      >
        <Paper
          className={styles.topbarPanel}
          radius={0}
          withBorder={false}
        >
          <Group
            mih={56}
            py={8}
            px="lg"
            justify="space-between"
            align="center"
          >
            {/* Left Section */}
            <Group gap="md" align="center">
              <ActionIcon
                className={styles.toggle}
                variant="light"
                color="blue"
                size="lg"
                radius="md"
                onClick={onToggleSidebar}
                aria-label={t('topbar.toggleSidebar')}
              >
                {sidebarVisible ? (
                  <IconChevronLeft size={20} style={{ color: '#2F3289' }} />
                ) : (
                  <IconChevronRight size={20} style={{ color: '#2F3289' }} />
                )}
              </ActionIcon>

              <Group gap="xs" align="center">
                <Box
                  className={styles.logo}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img 
                    src={zeusLogo} 
                    alt="Zeus Logo" 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Box>
                <Text 
                  className={styles.brandTitle}
                  size="lg" 
                  fw={700}
                >
                  {t('topbar.title')}
                </Text>
              </Group>
            </Group>

            {/* Right Section */}
            <Group gap="md" align="center">
              {/* 🔑 Client context selector for admin_globale */}
              {isGlobalAdmin() && clients.length > 0 && (
                <Group gap="xs" align="center">
                  <Select
                    size="xs"
                    placeholder="Seleziona cliente"
                    leftSection={<IconBuilding size={14} />}
                    value={pendingClientId ? String(pendingClientId) : null}
                    onChange={(value) => setPendingClientId(value ? parseInt(value, 10) : null)}
                    data={clients.map((c) => ({ value: String(c.id), label: c.name }))}
                    searchable
                    styles={{
                      root: { minWidth: 180, maxWidth: 250 },
                      input: {
                        fontSize: '12px',
                        background: 'rgba(47, 50, 137, 0.05)',
                        border: '1px solid rgba(47, 50, 137, 0.2)',
                      }
                    }}
                  />
                  {pendingClientId !== null && pendingClientId !== selectedClientId && (
                    <Tooltip label="Applica cambio tenant" withArrow>
                      <Button
                        className={ui.primaryAction}
                        size="xs"
                        variant="filled"
                        leftSection={<IconSwitch size={14} />}
                        onClick={() => setSelectedClientId(pendingClientId)}
                      >
                        Cambia Tenant
                      </Button>
                    </Tooltip>
                  )}
                </Group>
              )}
              <OperatorStatusToggle canHandover={canHandover} />
              <HandoverNotification />
              <LanguageSwitcher />

              <ActionIcon
                className={styles.logout}
                variant="light"
                color="red"
                size="lg"
                radius="md"
                onClick={onLogout}
                aria-label={t('topbar.logout')}
              >
                <IconLogout size={18} style={{ color: '#ef4444' }} />
              </ActionIcon>
            </Group>
          </Group>
        </Paper>
      </Box>
    </>
  );
}
